<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive, computed, onUnmounted} from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { AppScreens } from "../../Enums/ChoiceEnums";
    import { TableRowHighlight, TableRowType, ButtonDisplayStyle } from "../../Enums/UiEnums";
    import { ICol, IRowSelection, ValueStrDelegate } from "../../lib/ChoiceTableHelpers";
    import { IQuickPayReceiptItem, IQuickPayInsuredItem, IQuickPayReceiptAppointment, IQuickPayReceiptResponsibleParty,
             IQuickPayReceiptPaymentType, IQuickPayReceiptPaymentMethod, IQuickPayReceiptRecord, IQuickPayReceiptDefaultItem, IQuickPayReceiptCharge, PaymentBatchPosting } from '../../lib/Models/QuickPayModel';
    import { displayActiveReport } from '../../lib/Models/ActiveReportModel';
    import { ISearchPaymentPlanKey, ISearchPaymentPlanItem, ISearchPaymentBatchKey, ISearchPaymentBatchItem } from '../../lib/Models/SearchModel';
    import { IServiceLocationSearchItem } from '../../lib/Api/ServiceLocation';    
    import { ModalStyles, popAlert } from "../../lib/Notification";
    import { openWindowFromVue } from '../../lib/Models/CommonItemsModel';    
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { ButtonDisplaySize } from "../../Enums/UiEnums";
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import Combo from "../../components/Combo.vue";
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import FloatInput from '../../components/FloatInput.vue'; 
    import TextInput from '../../components/TextInput.vue'; 
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import SearchPaymentPlan from '../../components/Search/SearchPaymentPlan.vue';
    import SearchPaymentBatch from '../../components/Search/SearchPaymentBatch.vue';
    import SearchServiceLocation from '../../components/Search/SearchServiceLocation.vue';
    import * as ChoiceApi from '../../lib/ChoiceApi';

    const privilegesStore = usePrivilegesStore(initPinia());
    const copayDescription = "Co-Pay";
    const generalPaymentDescription = "General Payment";
    
    const props = defineProps<{
        patientId: number,
        patientEid: number,
        appointmentId?: number,
        scheduleDate?: string,
        tabId: number,
        receiptId?: number
    }>();

    const emit=defineEmits(["refreshBalance"]); 

    const searchPaymentPlanResults = ref<string>("");
    const searchPaymentBatchResults = ref<string>("");

    const formatPaidAmount: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
            return `$${row?.paidAmount ?? ""}`;
        }
    }

    const insuredColumns = ref<ICol[]>([
        { Name: 'Coverage', DataField: 'coverage' },
        { Name: 'Payer', DataField: 'payerName' },
        { Name: 'Insured ID', DataField: 'idCode' },
        { Name: 'Group/Policy Number', DataField: 'groupPolicyNumber' },
        { Name: 'Copay', DataField: 'copay' },
    ]);

    const receiptColumns = ref<ICol[]>([
        { Name: 'Amount Paid', DataField: 'paidAmount', FormatValue: formatPaidAmount },
        { Name: 'Post Date', DataField: 'paymentDate' },
        { Name: 'Paid By', DataField: 'paidByName' },
        { Name: 'Type', DataField: 'paymentTypeDescription' },
        { Name: 'Method', DataField: 'paymentMethodDescription' },
        { Name: 'ID #', DataField: 'paymentMethodIdNumber' },
        { Name: 'Comment', DataField: 'comment' },
        { Name: 'User ID', DataField: 'userId' },
    ]);

    const formData = reactive<{
        appointment?: IQuickPayReceiptAppointment,
        charge?: IQuickPayReceiptCharge,
        receiptList: IQuickPayReceiptItem[],
        insuredList: IQuickPayInsuredItem[],
        responsiblePartyList: IQuickPayReceiptResponsibleParty[],
        paymentTypeList: IQuickPayReceiptPaymentType[],
        paymentMethodList: IQuickPayReceiptPaymentMethod[],
        defaultList: IQuickPayReceiptDefaultItem[],
        selectedReceiptRow?: IQuickPayReceiptItem,
        receiptRecord: IQuickPayReceiptRecord,
        defaultPaymentPlanKey?: ISearchPaymentPlanKey,
        defaultPaymentBatchKey?: ISearchPaymentBatchKey,
        paymentPlanKey: number,
        paymentBatchKey: number,
        defaultServiceLocationText?: string,
        paidAmount: number,
        paymentBatchData?: string,
        paymentPlanData?: string,
        defaultPaymentPlanMethodId?: number,
        defaultPaymentPlanTypeId?: number 
    }>({
        receiptList: [],
        insuredList: [],
        responsiblePartyList: [],
        paymentTypeList: [],
        paymentMethodList: [],
        defaultList: [],
        receiptRecord: { 
            appointmentId: props.appointmentId ?? -1,
            patientEid: props.patientEid,
            comment: "",
            includeClaim: false,
            includeProcedure: false,
            includeDiagnosis: false,
            includeComment: false },
        paymentPlanKey: 0,
        paymentBatchKey: 0,
        paidAmount: 0.00
    });

    async function fetchData() {
        const promises = [];
        promises.push(privilegesStore.getScreens([AppScreens.Receipt, AppScreens.StatementDetails]));
        promises.push(ChoiceApi.quickPay.Receipt(props.patientId, props.appointmentId, props.receiptId).then(resp => {
            formData.appointment = resp?.appointment;
            formData.charge = resp?.charge;
            formData.receiptList = resp?.receiptList ?? [];
            formData.insuredList = resp?.insuredList ?? [];
            formData.responsiblePartyList = resp?.responsiblePartyList ?? [];
            formData.paymentTypeList = resp?.paymentTypeList ?? [];
            formData.paymentMethodList = resp?.paymentMethodList ?? [];
            formData.defaultList = resp?.defaultList ?? [];
            formData.defaultServiceLocationText = resp?.appointment.serviceLocationName ?? "";
            formData.defaultPaymentPlanMethodId = resp?.defaultPaymentPlanIds.defaultPaymentPlanMethod;
            formData.defaultPaymentPlanTypeId = resp?.defaultPaymentPlanIds.defaultPaymentPlanType;
        }));
        await Promise.all(promises);
        clearNewReceipt();
    }
    fetchData();

    onMounted(async () => {
        MessageLib.Receipt.sink.performAction(performActionSink);
        MessageLib.Emr.sink.pageAction(pageActionSink); 
    });

    onUnmounted(async () => {
        MessageLib.Receipt.sink.performActionDeleteSink(performActionSink);
        MessageLib.Emr.sink.pageActionDeleteSink(performActionSink);
    });


    const includeCommentChecked = computed<boolean>(() => formData.defaultList.some(item => item.controlName == "chkIncludeStmt" && item.defaultValue == "true"));
    const includeProcedureChecked = computed<boolean>(() => formData.defaultList.some(item => item.controlName == "chkIncludeProcedure" && item.defaultValue == "true"));
    const includeDiagnosisChecked = computed<boolean>(() => formData.defaultList.some(item => item.controlName == "chkIncludeDiagnosis" && item.defaultValue == "true"));

    const isPaidByRequired = computed<boolean>(() => formData.defaultList.some(item => item.controlName == "cmbPaidBy" && (item.requiredText ?? "") != ""));
    const isPaymentTypeRequired = computed<boolean>(() => formData.defaultList.some(item => item.controlName == "cmbPaymentType" && (item.requiredText ?? "") != ""));
    const isPaymentMethodRequired = computed<boolean>(() => formData.defaultList.some(item => item.controlName == "cmbPaymentMethod" && (item.requiredText ?? "") != ""));
    const isAmountRequired = computed<boolean>(() => formData.defaultList.some(item => item.controlName == "txtPaid" && (item.requiredText ?? "") != ""));
    const isPaymentBatchRequired = computed<boolean>(() => formData.charge?.paymentBatch == PaymentBatchPosting.Required);
    const isPaymentBatchVisible = computed<boolean>(() => formData.charge?.paymentBatch == PaymentBatchPosting.On || formData.charge?.paymentBatch == PaymentBatchPosting.Required);

    const isReverseReceiptEnabled = computed<boolean>(() => (formData.selectedReceiptRow ? true : false) && canCreateReceipt.value);
    const canCreateReceipt = computed<boolean>(() => privilegesStore.screensWithWritePrivileges[AppScreens.Receipt]);

    const isCreditCardSelected = computed<boolean>(() => formData.paymentMethodList.some(item => item.paymentMethodId == formData.receiptRecord.paymentMethodId && item.creditCard));
    const isCopay = computed<boolean>(() => formData.paymentTypeList.some(item => item.paymentTypeId == formData.receiptRecord.paymentTypeId && item.paymentTypeDesc == copayDescription));

    const defaultPaymentType = computed<number | undefined>(()=>  {
        const defaultItem = formData.defaultList.find(item => item.controlName == "cmbPaymentType");
        if (!defaultItem?.defaultValue)
            return formData.paymentTypeList.length > 0 ? formData.paymentTypeList[0].paymentTypeId : undefined;
        return +defaultItem.defaultValue;
    });
    const defaultPaymentMethod = computed<number | undefined>(()=>  {
        const defaultItem = formData.defaultList.find(item => item.controlName == "cmbPaymentMethod");
        if (!defaultItem?.defaultValue)
            return undefined;
        return +defaultItem.defaultValue;
    });

    function clearNewReceipt() {
        let serviceLocationEid = formData.appointment?.serviceLocationEid;
        let useDefault = true;
        if (formData.receiptRecord?.serviceLocationEid) {
            useDefault = false;
            serviceLocationEid = formData.receiptRecord?.serviceLocationEid;
        }

        formData.receiptRecord = {
            receiptBatchId: formData.appointment?.receiptBatchId,
            appointmentId: props.appointmentId ?? -1,
            patientEid: props.patientEid,
            comment: "",
            paidAmount: "0.00",
            includeClaim: false,
            includeProcedure: includeProcedureChecked.value,
            includeDiagnosis: includeDiagnosisChecked.value,
            includeComment: includeCommentChecked.value,
            serviceLocationEid: serviceLocationEid,
            paidByEid: formData.responsiblePartyList.length > 0 ? formData.responsiblePartyList[0].rpEntityId : undefined,
            paymentTypeId: defaultPaymentType.value,
            paymentMethodId: defaultPaymentMethod.value
        }
        if (useDefault)
            formData.defaultServiceLocationText = formData.appointment?.serviceLocationName ?? ""; 
        formData.paymentPlanKey += 1;
        formData.paymentBatchKey += 1;
        if (formData.receiptRecord.paymentTypeId == (formData.paymentTypeList.filter(item => item.paymentTypeDesc == copayDescription)[0].paymentTypeId)) {

            if ((formData.receiptList.filter(item => item.paymentTypeDescription == copayDescription).length) == 0) {
                formData.paidAmount = getDefaultCopayAmount();
            }
            else {
                formData.paidAmount = 0.00; 
                formData.receiptRecord.paymentTypeId = formData.paymentTypeList.filter(item => item.paymentTypeDesc == generalPaymentDescription)[0].paymentTypeId;
            }
        }
        else {
            formData.paidAmount = 0.00; 
        }
    }
    function onReceiptClick(rowData: IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : void {
        formData.selectedReceiptRow = <IQuickPayReceiptItem> rowData;
    }

    async function onHistory() {
        const windowId = AppScreens.TransactionHistory;
        const windowUrl = `/officemd/screens/transactionHistory.asp?PatientID=${props.patientEid}`;
        const windowSettings = { noTimestamp: "Y" };
        openWindowFromVue(windowId, windowUrl, windowSettings);        
    }
    
    async function onPayments() {
        if (formData.charge?.claimId) {
            const windowId = AppScreens.PaymentEntry;
            const windowUrl = `/officemd/screens/paymententry.htm?ClaimId=${formData.charge.claimId}`;
            const windowSettings = { noTimestamp: "Y", height: 500, width: 750 };
            openWindowFromVue(windowId, windowUrl, windowSettings);        
        }
        else {
            popAlert("No claim exists at this time.", "Payments", ModalStyles.info);
            return;
        }
    }
    
    async function onValidate() {
        const validationMessage = await ChoiceApi.patient.ValidatePatient(props.patientEid, 0);
        if (validationMessage)
            popAlert(validationMessage.replaceAll("\r\n", "<br/>"), "Validate", ModalStyles.info);
    }

    async function onReverseReceipt() {
        if (privilegesStore.screensWithUpdatePrivileges[AppScreens.Receipt]) {
            if (!formData.selectedReceiptRow) {
                popAlert("You must select a receipt to reverse.", "Receipt Reversal", ModalStyles.info);
                return;
            }
            else {
                const windowId = AppScreens.ReceiptReversal;
                const windowUrl = `/officemd/screens/ReceiptReversal.htm?ID=${formData.selectedReceiptRow.receiptId}`;
                const windowSettings = { noTimestamp: "Y", height: 520, width: 450 };
                openWindowFromVue(windowId, windowUrl, windowSettings);        
            }
        }
        else {
            popAlert("You do not have access to perform this function. Please contact your System Administrator.", "Receipt Reversal", ModalStyles.info);
            return
        }
    }
    async function onPrintReceipt() {  
        const reportInputs = 
        { 
            batchId: formData.receiptRecord.receiptBatchId ?? 0,
            appointmentId: formData.appointment?.appointmentId,
            printProcedure: formData.receiptRecord.includeProcedure,
            printDiagnosis: formData.receiptRecord.includeDiagnosis,
            patientId: props.patientId
        };

        const result = await ChoiceApi.activeReport.Receipt(reportInputs);
        displayActiveReport(result, 600, 800, "Print Receipt", props.patientId);      
    }

    async function onCreateReceipt() {
        if (!validateSave(false))
            return;

        if (!await buildReceiptBatchId())
            return;

        const result = await ChoiceApi.quickPay.ReceiptCreate(formData.receiptRecord);
        if (!result?.passed) {
            popAlert(result?.message ?? "Failed to create the receipt", "Create Receipt", ModalStyles.danger);
            return;
        }
        if (!formData.appointment) 
            return;
        formData.appointment.receiptBatchId = result.receiptBatchId;
        await onRefreshReceipts();
        clearNewReceipt();
    }

    async function onRefreshReceipts() {
        const promises = [];
        promises.push(ChoiceApi.quickPay.ReceiptList(props.patientId, props.appointmentId, props.receiptId, formData?.appointment?.receiptBatchId).then(resp => {
            formData.receiptList = resp ?? [];
        }));
        promises.push(ChoiceApi.quickPay.ReceiptCharge(props.patientId, props.appointmentId, props.receiptId, formData?.appointment?.receiptBatchId).then(resp => {
            formData.charge = resp;
        }));
        await Promise.all(promises); 
        emit("refreshBalance");       
    }

    async function buildReceiptBatchId() {
        if (!formData.receiptRecord.receiptBatchId) {
            const created = await ChoiceApi.quickPay.ReceiptBatchIdCreate(props.patientId, props.appointmentId, props.receiptId);
            if (!created?.receiptBatchId) {
                popAlert("Failed to create the receipt", "Create Receipt", ModalStyles.danger);
                return false;
            }
            formData.receiptRecord.receiptBatchId = created.receiptBatchId;
            if (formData.appointment)
                formData.appointment.receiptBatchId = created.receiptBatchId;
        }
        return true;
    }

    function validateSave(isTakePayment: boolean) {
        if (formData.receiptRecord.paidAmount)
            formData.receiptRecord.paidAmount = formData.paidAmount.toLocaleString()

        if (formData.paidAmount <= 0 && !formData.charge?.zeroReceiptsAllowed && !isTakePayment) {
            popAlert("An amount is required for saving a receipt", "Create Receipt Required", ModalStyles.info);
            return false;
        }        

        let fields = [];
        if (formData.paidAmount <= 0 && isAmountRequired.value && isTakePayment)
            fields.push("Amount");
        if (!formData.receiptRecord.paidByEid && isPaidByRequired.value)
            fields.push("Paid By");
        if (!formData.receiptRecord.paymentTypeId && isPaymentTypeRequired.value)
            fields.push("Type");
        if (!formData.receiptRecord.paymentMethodId && isPaymentMethodRequired.value)
            fields.push("Method");
        if ((!formData.receiptRecord.serviceLocationEid))
            fields.push("Service Location");
        if ((!formData.receiptRecord.paymentBatchId || formData.receiptRecord.paymentBatchId == 0) && isPaymentBatchRequired.value)
            fields.push("Payment Batch");
        if (fields.length > 0) {
            popAlert(`The following fields are required: ${fields.join(", ")}`, "Create Receipt Required", ModalStyles.info);
            return false;
        }
        return true;
    }

    async function onTakePayment() {
        if (!validateSave(true))
            return;

        if (!await buildReceiptBatchId())
            return;

        const paymentData = 
        { 
            useReceipt: true,
            requirePayment: true,
            childCallback: true,
            paidByType: 1, 
            paidByID: props.patientId, 
            paidByName: formData.appointment?.patientName,
            appointmentID: formData.appointment?.appointmentId, 
            paidByEID: formData.receiptRecord.paidByEid, 
            serviceLocationEID: formData.receiptRecord.serviceLocationEid,
            paymentMethod: formData.receiptRecord.paymentMethodId,
            paymentType: formData.receiptRecord.paymentTypeId,
            receiptAmount: formData.paidAmount,
            comment: formData.receiptRecord.comment ?? "",
            batch: formData.receiptRecord.receiptBatchId,
            claimIndicator: formData.receiptRecord.includeClaim ? "Y" : "N",
            commentIndicator: formData.receiptRecord.includeComment ? "Y" : "N",
            procedureIndicator: formData.receiptRecord.includeProcedure ? "Y" : "N",
            diagnosisIndicator: formData.receiptRecord.includeDiagnosis ? "Y" : "N",
            copayIndicator: isCopay.value,
            paymentBatchID: (formData.receiptRecord.paymentBatchId === 0 ? undefined : formData.receiptRecord.paymentBatchId),
            paymentBatchData: (formData.receiptRecord.paymentBatchId === 0 ? undefined : formData.paymentBatchData),
            paymentPlanID: formData.receiptRecord.paymentPlanId,
            paymentPlanData : formData.paymentPlanData,
        };
        await ChoiceApi.quickPay.TakePayment(paymentData);
    }

    async function onTextToPay() {
        let fields = [];
        if (formData.paidAmount <= 0 && isAmountRequired.value)
            fields.push("Amount");
        if (fields.length > 0) {
            popAlert(`The following fields are required: ${fields.join(", ")}`, "Create Receipt Required", ModalStyles.info);
            return;
        }
        const qs = `pid=${props.patientId}&pname=${formData.appointment?.patientName}&amt=${formData.paidAmount}&edit=false`;
        const windowId = 'PSTextToPay';
        const windowUrl = `/officemd/screens/PaySimpleTextToPay.htm?${qs}`;
        const windowSettings = { noTimestamp: "Y" };
        openWindowFromVue(windowId, windowUrl, windowSettings);
    }

    async function onQuickStatement() {
        if (privilegesStore.screensWithUpdatePrivileges[AppScreens.StatementDetails]) {
            const windowId = AppScreens.StatementDetails;
            const windowUrl = `/officemd/screens/statementdetails.htm?pat_eid=${props.patientEid}&rp_eid=0`;
            const windowSettings = { noTimestamp: "Y" };
            openWindowFromVue(windowId, windowUrl, windowSettings);        
        }
        else {
            popAlert("You do not have access to perform this function. Please contact your System Administrator.", "Quick Statement", ModalStyles.info);
            return;
        }
    }

    async function onEReceipt() {
        if (!formData.selectedReceiptRow) {
            popAlert("A receipt must first be selected", "E-Receipt", ModalStyles.info);
            return;
        }
        const windowId = AppScreens.EReceiptCommunication;
        const windowUrl = `/officemd/screens/Vue/EReceiptCommunication.htm?pat_eid=${props.patientEid}&amt=${formData.selectedReceiptRow?.paidAmount}`;
        const windowSettings = { noTimestamp: "Y" };
        openWindowFromVue(windowId, windowUrl, windowSettings);
    }

    async function onChangeServiceLocation(serviceLocation: IServiceLocationSearchItem) {
        if (serviceLocation)
            formData.receiptRecord.serviceLocationEid = serviceLocation.entity_id;
        else 
            formData.receiptRecord.serviceLocationEid = undefined;
    }

    function onChangePaymentPlan(paymentPlan: ISearchPaymentPlanItem) : void {
        let setPaymentPlanData = false;
        if (paymentPlan) {
            formData.receiptRecord.paymentPlanId = paymentPlan.paymentPlanId;
            const planAmount = +parseFloat(paymentPlan.installmentAmount?.replace(/[^0-9.]/g, "") ?? "0").toFixed(2);
            if (planAmount > 0) {
                setPaymentPlanData = true;
                formData.paidAmount = planAmount; 
                formData.paymentPlanData = paymentPlan.display;
                formData.receiptRecord.paymentTypeId = formData.defaultPaymentPlanTypeId;
                formData.receiptRecord.paymentMethodId = formData.defaultPaymentPlanMethodId; 
            }
        }
        if (!setPaymentPlanData) {
            formData.paidAmount = 0.00;
            formData.receiptRecord.paymentPlanId = undefined;
            formData.paymentPlanData = undefined;
            formData.receiptRecord.paymentTypeId = defaultPaymentType.value;
            formData.receiptRecord.paymentMethodId = defaultPaymentMethod.value; 
        } 
        searchPaymentPlanResults.value = JSON.stringify(paymentPlan);
    }

    function onChangePaymentBatch(paymentBatch: ISearchPaymentBatchItem) : void {
        if (paymentBatch) {
            formData.receiptRecord.paymentBatchId = paymentBatch.paymentBatchId;
            formData.paymentBatchData = paymentBatch.paymentBatchDate;
        }
        else {
            formData.receiptRecord.paymentBatchId = undefined;
            formData.paymentBatchData = undefined;
        } 
        searchPaymentBatchResults.value = JSON.stringify(paymentBatch);
    }

	async function performActionSink(payload: any) {
        if (!payload?.action)
            return;

        let myIdentifier = false;
        if (payload?.patientId == props.patientId) {
            myIdentifier = true;
        }
        else if (payload.receiptId) {
            myIdentifier = formData.receiptList.some(item => item.receiptId == payload.receiptId);
        }
        if (!myIdentifier)
            return;

        if (payload.action == "refresh") {
            await onRefreshReceipts();
        }
        else if (payload.action == "refreshCharges") {
            await refreshCharges();
        }
        else if (payload.action == "refreshAll") {
            await onRefreshReceipts();
            await refreshCharges();
            clearNewReceipt();
        }
	}

	async function pageActionSink(payload: MessageLib.Emr.EmrPageActionPayload) {
        if (payload.location == AppScreens.EMRSuperbill) {
            if (payload.action == "Refresh_PatientBalance" && payload.data == props.patientId) {
                await refreshCharges();
            }
        }
    }

    async function refreshCharges() {
        formData.charge = await ChoiceApi.quickPay.ReceiptCharge(props.patientId, props.appointmentId, props.receiptId);
        emit("refreshBalance");
    }

    async function onChangePaymentMethod(selectedItem : IQuickPayReceiptPaymentMethod) : Promise<void> {
        if (selectedItem.creditCard) {
            formData.receiptRecord.idNumber = undefined;
        }
    }
    async function onChangePaymentType(selectedItem : IQuickPayReceiptPaymentType) : Promise<void> {
        if (selectedItem.paymentTypeDesc == copayDescription) {
            formData.paidAmount = getDefaultCopayAmount();
        }
    }

    function getDefaultCopayAmount() {
        const index = formData.insuredList.findIndex(item => item.coverage == 1);
        if (index < 0)
            return 0;
        const is_percent = formData.insuredList[index].copay.includes("%"); 
        const copay = formData.insuredList[index].copay.replace(/[$%]/g, "");
        if (isNaN(Number(copay))) {
            return 0;
        }
        if (is_percent) {
            return parseFloat((parseFloat((formData.charge?.chargeBalance ?? "0").replace(/[$]/g, "")) * parseFloat(copay) / 100.00).toFixed(2));
        }
        return parseFloat(copay);
    };

</script>

<style scoped>
    .labelMediumWidth {
        width: 78px;
    }
    .amountWidth {
        width: 100px
    }
</style>

<template>
    <div class="row mt-2">
        <div class="col fw-bold label-isalus-medium isalus-section-header">
            Appointment Details
        </div>
    </div>
    <div class="row mt-2 ps-3">
        <div class="col-1 label-isalus-small isalus-label-secondary">
            ID
        </div>
        <div class="col label-isalus-small isalus-label-secondary">
            Date & Time
        </div>
        <div class="col label-isalus-small isalus-label-secondary">
            Resource Name
        </div>
        <div class="col label-isalus-small isalus-label-secondary">
            Primary Care Physician
        </div>
        <div class="col label-isalus-small isalus-label-secondary">
            Service Location
        </div>
        <div class="w-100"></div>
        <div class="col-1 label-isalus-small">
            {{ formData.appointment?.appointmentId }}
        </div>
        <div class="col label-isalus-small">
            {{ formData.appointment?.appointmentDisplay }}
        </div>
        <div class="col label-isalus-small">
            {{ formData.appointment?.resourceName }}
        </div>
        <div class="col label-isalus-small">
            {{ formData.appointment?.primaryCarePhysician ?? "None Reported" }}
        </div>
        <div class="col label-isalus-small">
            {{ formData.appointment?.serviceLocationName }}
        </div>
    </div>
    <div class="row mt-2 ps-3">
        <div class="col">
            <ActionButtonV2 
                :action="ActionButtonStyle.Open"
                :text="'Transaction History'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Opening'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onHistory"
                :endingPadding="1"/>
            <ActionButtonV2 
                :action="ActionButtonStyle.Open"
                :text="'Payments'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Opening'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onPayments"
                :endingPadding="1"/>
            <ActionButtonV2 
                :action="ActionButtonStyle.Check"
                :text="'Validate'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Validating'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onValidate"
                :endingPadding="1"/>
            <ActionButtonV2 
                :action="ActionButtonStyle.Refresh"
                :text="'Refresh Charges'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Refreshing'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="refreshCharges"/>
        </div>
    </div>
    <div class="row mt-3">
        <div class="col fw-bold label-isalus-medium isalus-section-header">
            Insurance Information
        </div>
    </div>
    <div class="row mt-2 ps-3">
        <div class="col">
            <ChoiceTable 
                :columns="insuredColumns" 
                :data="formData.insuredList" 
                :show-filters="false" 
                :allowSorting="false"
                fontSize="smaller">
            </ChoiceTable>
        </div>
    </div>
    <div class="row mt-2">
        <div class="col fw-bold label-isalus-medium isalus-section-header">
            New Receipt Detail
        </div>
    </div>
    <div v-if="formData.charge?.claimId" class="row mt-2 ps-3">
        <div class="col-auto label-isalus-small">
            Claim:
        </div>
        <div class="col label-isalus-small">
            {{ formData.charge?.claimId }}
        </div>
        <div class="col-auto label-isalus-small">
            Total:
        </div>
        <div class="col label-isalus-small">
            {{ formData.charge?.claimTotal }}
        </div>
        <div class="col-auto label-isalus-small">
            Balance:
        </div>
        <div class="col label-isalus-small">
            {{ formData.charge?.chargeBalance }}
        </div>
        <div class="col-auto label-isalus-small">
            Charge Rate:
        </div>
        <div class="col label-isalus-small">
            {{ formData.charge?.chargePercent }}%
        </div>
        <div class="w-100"></div>
        <div class="col label-isalus-small">
            Appointment Estimated Balance: ${{ formData.charge?.estimatedBalance }} {{ formData.charge?.estimatedBalanceMessage }}
        </div>
    </div>
    <div v-else-if="formData.appointment?.appointmentId" class="row mt-2 ps-3">
        <div class="col label-isalus-small fst-italic">
            No claim is attached to this appointment, current Charge Rate is {{ formData.charge?.chargePercent }}%
        </div>
    </div>
    <div v-else class="row mt-2 ps-3">
        <div class="col label-isalus-small fst-italic">
            Appointment Estimated Balance: ${{ formData.charge?.estimatedBalance }}. {{ formData.charge?.estimatedBalanceMessage }}
        </div>
    </div>
    <div class="row mt-2 ps-3">
        <div class="col">
            <div class="row">
                <div class="labelMediumWidth label-isalus-small mt-2 pe-0">
                    Amount <span v-if="isAmountRequired" class="text-danger">*</span>   
                </div>
                <div class="amountWidth ps-0">
                    <FloatInput
                        v-model:numberValue="formData.paidAmount"
                        :maxLength="10"
                        :startingRange="0"
                        :minPrecision="2"
                        :maxPrecision="2"
                        :positive="true"
                        :errorDisplay="false"
                        :cssFramework="'vanilla'"
                        placeholder=""/>
                </div>
                <div class="w-100 mt-1"></div>
                <div class="labelMediumWidth label-isalus-small mt-2">
                    Paid By <span v-if="isPaidByRequired" class="text-danger">*</span>
                </div>
                <div class="col ps-0">
                    <Combo 
                        :optionList="formData.responsiblePartyList"
                        optionValue="rpEntityId"
                        optionDescription="rpName"
                        v-model:selected="formData.receiptRecord.paidByEid" /> 
                </div>
                <div class="w-100 mt-1"></div>
                <div class="row pe-0">
                    <div class="labelMediumWidth label-isalus-small mt-2">
                        Type <span v-if="isPaymentTypeRequired" class="text-danger">*</span>
                    </div>
                    <div class="col ps-0 pe-0">
                        <Combo 
                            :optionList="formData.paymentTypeList"
                            optionValue="paymentTypeId"
                            optionDescription="paymentTypeDesc"
                            v-model:selected="formData.receiptRecord.paymentTypeId"
                            @onChange="onChangePaymentType" />                         
                    </div>
                    <div class="labelMediumWidth label-isalus-small mt-2">
                        Method <span v-if="isPaymentMethodRequired" class="text-danger">*</span>
                    </div>
                    <div class="col ps-0 pe-0">
                        <Combo 
                            :optionList="formData.paymentMethodList"
                            optionValue="paymentMethodId"
                            optionDescription="paymentMethodDesc"
                            v-model:selected="formData.receiptRecord.paymentMethodId"
                            @onChange="onChangePaymentMethod" />                         
                    </div>
                </div>
                <div class="w-100 mt-1"></div>
                <div class="labelMediumWidth label-isalus-small mt-2">
                    ID #
                </div>
                <div class="col ps-0">
                    <TextInput 
                        v-model:textValue="formData.receiptRecord.idNumber" 
                        :maxLength="50"
                        :disabled="isCreditCardSelected"
                        :cssFramework="'vanilla'"  
                        placeholder="" 
                    />
                </div>
                <div class="w-100 mt-1"></div>
                <div class="labelMediumWidth label-isalus-small mt-2">
                    Comment
                </div>
                <div class="col ps-0">
                    <TextInput 
                        v-model:textValue="formData.receiptRecord.comment" 
                        :rows=2
                        :maxLength="150"
                        :cssFramework="'vanilla'"
                        :hideTranscribe="true"
                        placeholder="" 
                    />
                </div>
                <div class="w-100"></div>
                <div class="col">
                    <Checkbox v-model="formData.receiptRecord.includeClaim"/>  
                    <span label class="ps-1 pe-3 label-isalus-small" title="Receipt Associated with Claim">Associated with Claim</span>                    
                </div>
                <div class="col ps-0">
                    <Checkbox v-model="formData.receiptRecord.includeComment"/>  
                    <span label class="ps-1 pe-3 label-isalus-small" title="Include Comment on Receipt">Include Comment</span>                    
                </div>
                <div class="w-100"></div>
                <div class="col">
                    <Checkbox v-model="formData.receiptRecord.includeDiagnosis"/>  
                    <span label class="ps-1 pe-3 label-isalus-small" title="Include Diagnosis on Receipt">Include Diagnosis</span>                    
                </div>
                <div class="col ps-0">
                    <Checkbox v-model="formData.receiptRecord.includeProcedure"/>  
                    <span label class="ps-1 pe-3 label-isalus-small" title="Include Procedure on Receipt">Include Procedure</span>                    
                </div>
            </div>
        </div>
        <div class="col">
            <div class="row">
                <div class="col">
                    <div class="label-isalus-small">
                        Service Location <span class="text-danger">*</span>
                    </div>
                    <SearchServiceLocation 
                        v-model:defaultSearchText="formData.defaultServiceLocationText"
                        label=""
                        :show-clear-search="true" 
                        :addToBaseClass="'label-isalus-small'" 
                        @onChange="onChangeServiceLocation" />
                </div>
                <div class="w-100"></div>
                <div class="col">
                    <SearchPaymentPlan 
                        :patientID="props.patientId"
                        :key="formData.paymentPlanKey"
                        label="Payment Plan"
                        :defaultSearchKey="formData.defaultPaymentPlanKey" 
                        :addToBaseClass="'label-isalus-small'" 
                        :showClearSearch="true"
                        :enableAutoSearch="true"
                        :new-button="true"
                        :displayPlanMessage="true"
                        @onChange="(val) => onChangePaymentPlan(val)" />
                </div>
                <div v-if="isPaymentBatchVisible" class="w-100 mt-1"></div>
                <div v-if="isPaymentBatchVisible" class="col">
                    <div class="label-isalus-small">
                        Payment Batch <span v-if="isPaymentBatchRequired" class="text-danger">*</span>
                    </div>
                    <SearchPaymentBatch 
                        :key="formData.paymentBatchKey"
                        label=""
                        :defaultSearchKey="formData.defaultPaymentBatchKey" 
                        :addToBaseClass="'label-isalus-small'" 
                        :showClearSearch="true"
                        :enableAutoSearch="true"
                        :new-button="true"
                        @onChange="(val) => onChangePaymentBatch(val)">
                    </SearchPaymentBatch>
                </div>
            </div>
        </div>
    </div>
    <div class="row mt-2 ps-3">
        <div class="col">
            <ActionButtonV2 
                :action="ActionButtonStyle.Add"
                :text="'Create Receipt'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Creating'"
                :disabled="!canCreateReceipt"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onCreateReceipt"
                :endingPadding="1" />         
            <ActionButtonV2 
                v-if="isCreditCardSelected"
                :action="ActionButtonStyle.Add"
                :text="'Take Payment'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Taking Payment'"
                :disabled="!canCreateReceipt"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onTakePayment"
                :endingPadding="1" />
            <ActionButtonV2 
                v-if="isCreditCardSelected"
                :action="ActionButtonStyle.Add"
                :text="'Text to Pay'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Opening'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onTextToPay" />
        </div>
    </div>

    <div class="row mt-3">
        <div class="col fw-bold label-isalus-medium isalus-section-header">
            Receipt Transactions
        </div>
    </div>
    <div class="row mt-2 ps-3">
        <div class="col">
            <ChoiceTable 
                :columns="receiptColumns" 
                :data="formData.receiptList" 
                :show-filters="false" 
                :allowSorting="false"
                fontSize="smaller"
                :rowHighlight="TableRowHighlight.onSingleClick"
                @rowclick="onReceiptClick"
                :key-value-data-member="'receiptId'">
            </ChoiceTable>
        </div>
    </div>
    <div class="row mt-2 ps-3">
        <div class="col-9">
            <ActionButtonV2 
                :action="ActionButtonStyle.Reverse"
                :text="'Reverse Receipt'"
                :size="ButtonDisplaySize.Smaller" 
                :disabled="!isReverseReceiptEnabled"
                :spinText="'Reverse'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onReverseReceipt"
                :endingPadding="1"/>
            <ActionButtonV2 
                :action="ActionButtonStyle.Print"
                :text="'Print Receipt'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Printing'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onPrintReceipt"
                :endingPadding="1"/>
            <ActionButtonV2 
                :action="ActionButtonStyle.Open"
                :text="'Quick Statement'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Generating'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onQuickStatement"
                :endingPadding="1"/>
            <ActionButtonV2 
                :action="ActionButtonStyle.Receipt"
                :text="'E-Receipt'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Generating'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onEReceipt"
                :endingPadding="1"/>
            <ActionButtonV2 
                :action="ActionButtonStyle.Refresh"
                :text="'Refresh'"
                :size="ButtonDisplaySize.Smaller" 
                :spinText="'Refreshing'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="onRefreshReceipts"/>
        </div>
        <div class="col text-end label-isalus-small me-2">
            Receipt Total: ${{ formData.charge?.receiptTotal ?? "0.00" }}
        </div>
    </div>
</template>

